import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface AuthContextType {
  isAuthenticated: boolean
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    // Authenticated if we hold an API token from a previous successful login.
    if (localStorage.getItem('adminToken')) {
      setIsAuthenticated(true)
    }
  }, [])

  // Credentials are validated server-side; on success the server returns an
  // opaque API token that authorises subsequent API calls. The password itself
  // is never stored in the bundle or in localStorage.
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      })

      if (!response.ok) {
        return false
      }

      const { token } = await response.json()
      if (!token) {
        return false
      }

      localStorage.setItem('adminToken', token)
      setIsAuthenticated(true)
      return true
    } catch {
      return false
    }
  }

  const logout = () => {
    setIsAuthenticated(false)
    localStorage.removeItem('adminToken')
  }

  const value = {
    isAuthenticated,
    login,
    logout
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
